import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { billingSnapshotForStay } from "@/core/billing/service-billing-status";
import {
  assignBedToStay,
  BED_STATUS_LABELS,
  bedDisplayLabel,
  countOccupiedBeds,
  countPendingStays,
  dischargeStay,
  HospitalizationError,
  listActiveStays,
  listAvailableBeds,
  listBedsWithLocation,
  listHospitalWards,
  STAY_STATUS_LABELS,
} from "@/core/hospitalization/hospitalization-store";
import { getPatientById, patientDetailPath } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { canAssignBeds } from "@/core/permissions";
import { useAuth } from "@itaas/core/auth/useAuth";
import { routes } from "@/router/all_routes";

export function HospitalizationPage() {
  const auth = useAuth();
  const canAssign = canAssignBeds(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [wardFilter, setWardFilter] = useState("");
  const [selectedStayId, setSelectedStayId] = useState("");
  const [selectedBedId, setSelectedBedId] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:hospitalization", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    void import("@/core/billing/extended-hospital-billing").then(
      ({ syncRecurringHospitalizationBills }) => syncRecurringHospitalizationBills(),
    );
    return () => {
      window.removeEventListener("hospitol:hospitalization", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
    };
  }, []);

  const wards = useMemo(() => {
    void refreshKey;
    return listHospitalWards();
  }, [refreshKey]);

  const beds = useMemo(() => {
    void refreshKey;
    return listBedsWithLocation(wardFilter || undefined);
  }, [refreshKey, wardFilter]);

  const pendingStays = useMemo(() => {
    void refreshKey;
    return listActiveStays().filter((s) => s.status === "pending");
  }, [refreshKey]);

  const activeStays = useMemo(() => {
    void refreshKey;
    return listActiveStays().filter((s) => s.status === "active");
  }, [refreshKey]);

  const allStays = useMemo(
    () => [...pendingStays, ...activeStays],
    [pendingStays, activeStays],
  );
  const staysPagination = useClientPagination(allStays, [], 25);
  const { pagedItems: pagedStays } = staysPagination;

  const availableBeds = useMemo(() => {
    void refreshKey;
    return listAvailableBeds(wardFilter || undefined);
  }, [refreshKey, wardFilter]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleAssign = () => {
    if (!canAssign || !selectedStayId || !selectedBedId) return;
    try {
      assignBedToStay(selectedStayId, selectedBedId);
      setMsg("Lit attribué.");
      setSelectedStayId("");
      setSelectedBedId("");
      setError(null);
      refresh();
    } catch (err) {
      setError(err instanceof HospitalizationError ? err.message : "Attribution impossible.");
    }
  };

  const handleDischarge = (stayId: string) => {
    if (!canAssign) return;
    try {
      dischargeStay(stayId);
      setMsg("Patient sorti — lit libéré.");
      setError(null);
      refresh();
    } catch (err) {
      setError(err instanceof HospitalizationError ? err.message : "Sortie impossible.");
    }
  };

  const occupied = countOccupiedBeds();
  const pending = countPendingStays();

  return (
    <div>
      <PageHeader
        title="Hospitalisation & lits"
        description="Plan des lits, séjours en cours et attribution depuis l'admission."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.patientConsumables} className="btn btn-outline-secondary btn-sm">
              Consommables patient
            </Link>
            <Link to={routes.nursingCare} className="btn btn-outline-secondary btn-sm">
              Soins infirmiers
            </Link>
          </div>
        }
      />

      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {error && <div className="alert alert-warning py-2">{error}</div>}

      <div className="row g-2 mb-3">
        <div className="col-6 col-md-4">
          <div className="ph-invoice-sheet h-100">
            <div className="small text-muted">Lits occupés</div>
            <div className="h4 mb-0">{occupied}</div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="ph-invoice-sheet h-100">
            <div className="small text-muted">Séjours sans lit</div>
            <div className="h4 mb-0">{pending}</div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="ph-invoice-sheet h-100">
            <div className="small text-muted">Lits libres</div>
            <div className="h4 mb-0">{availableBeds.length}</div>
          </div>
        </div>
      </div>

      {canAssign && pendingStays.length > 0 && (
        <div className="ph-invoice-sheet mb-3">
          <h3 className="h6 fw-semibold mb-3">Attribuer un lit</h3>
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label small fw-semibold" htmlFor="stay-select">
                Séjour en attente
              </label>
              <select
                id="stay-select"
                className="form-select form-select-sm"
                value={selectedStayId}
                onChange={(e) => setSelectedStayId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {pendingStays.map((stay) => {
                  const p = getPatientById(stay.patientId);
                  return (
                    <option key={stay.id} value={stay.id}>
                      {p ? patientFullName(p) : "—"} — {stay.motif}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-12 col-md-5">
              <label className="form-label small fw-semibold" htmlFor="bed-select">
                Lit disponible
              </label>
              <select
                id="bed-select"
                className="form-select form-select-sm"
                value={selectedBedId}
                onChange={(e) => setSelectedBedId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {availableBeds.map((row) => (
                  <option key={row.bed.id} value={row.bed.id}>
                    {row.ward.name} · {bedDisplayLabel(row.bed, row.room)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-2">
              <button
                type="button"
                className="btn btn-primary btn-sm w-100"
                disabled={!selectedStayId || !selectedBedId}
                onClick={handleAssign}
              >
                Attribuer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ph-invoice-sheet mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ph-hosp-ward-head">
          <h3 className="h6 fw-semibold mb-0">Plan des lits</h3>
          <select
            className="form-select form-select-sm w-100 w-md-auto"
            style={{ maxWidth: "14rem" }}
            value={wardFilter}
            onChange={(e) => setWardFilter(e.target.value)}
          >
            <option value="">Tous les services</option>
            {wards.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="row g-2">
          {beds.map((row) => {
            const patient = row.stay ? getPatientById(row.stay.patientId) : null;
            const statusClass =
              row.bed.status === "available"
                ? "border-success"
                : row.bed.status === "occupied"
                  ? "border-primary"
                  : row.bed.status === "reserved"
                    ? "border-warning"
                    : "border-secondary";
            return (
              <div key={row.bed.id} className="col-6 col-md-4 col-lg-3">
                <div className={`border rounded p-2 h-100 ${statusClass}`}>
                  <div className="small fw-semibold">{row.ward.name}</div>
                  <div className="small">{bedDisplayLabel(row.bed, row.room)}</div>
                  <div className="small text-muted mb-1">
                    {BED_STATUS_LABELS[row.bed.status]}
                  </div>
                  {patient && row.stay && (
                    <>
                      <div className="small fw-medium">
                        <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                      </div>
                      <div className="small text-muted">{row.stay.motif}</div>
                      {canAssign && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 mt-1"
                          onClick={() => handleDischarge(row.stay!.id)}
                        >
                          Sortie patient
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="ph-invoice-sheet">
        <h3 className="h6 fw-semibold mb-3">Séjours actifs</h3>
        {activeStays.length === 0 && pendingStays.length === 0 ? (
          <p className="text-muted small mb-0">
            Aucun séjour — orientez un patient vers &quot;Hospitalisation&quot; à l&apos;accueil.
          </p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table mb-0">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Service</th>
                  <th>Motif</th>
                  <th>Statut</th>
                  <th>Depuis</th>
                  <th>Facturation</th>
                </tr>
              </thead>
              <tbody>
                {pagedStays.map((stay) => {
                  const patient = getPatientById(stay.patientId);
                  return (
                    <tr key={stay.id} className="ph-diag-row">
                      <td data-label="Patient" className="fw-medium">
                        {patient ? (
                          <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-label="Service" className="small">
                        {stay.wardName}
                      </td>
                      <td data-label="Motif" className="small">
                        {stay.motif}
                      </td>
                      <td data-label="Statut">
                        <span className="badge text-bg-light border">
                          {STAY_STATUS_LABELS[stay.status]}
                        </span>
                      </td>
                      <td data-label="Depuis" className="small text-muted">
                        {new Date(stay.admittedAt).toLocaleString("fr-FR")}
                      </td>
                      <td data-label="Facturation">
                        <ServiceBillingBadge snapshot={billingSnapshotForStay(stay.id)} compact />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={staysPagination.page}
              pageSize={staysPagination.pageSize}
              totalItems={staysPagination.totalItems}
              onPageChange={staysPagination.setPage}
              onPageSizeChange={staysPagination.setPageSize}
            />
          </div>
        )}
      </div>
    </div>
  );
}
